import { trapFocus } from '@crown/framework/a11y';
import { effect } from '@crown/signal/effect';
import { ZOOM_FACTOR, ZOOM_STEP, type ZoomModel } from './zoom-model';

const CLOSE = 'M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z';
const ZOOM_IN = 'M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Zm7-3.5a.75.75 0 0 1 .75.75v2h2a.75.75 0 0 1 0 1.5h-2v2a.75.75 0 0 1-1.5 0v-2h-2a.75.75 0 0 1 0-1.5h2v-2A.75.75 0 0 1 9 5.5Z';
const RESET = 'M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z';
const ZOOM_OUT = 'M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Zm3.75.75a.75.75 0 0 1 0-1.5h6.5a.75.75 0 0 1 0 1.5h-6.5Z';

function IconButton({ label, d, onPress, className = 'docs-zoom-ctrl-btn' }: { readonly label: string; readonly d: string; readonly onPress: () => void; readonly className?: string }) @{
  <button type="button" class={className} aria-label={label} onClick={onPress}>
    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d={d}></path></svg>
  </button>
}

/** Wheel, drag, pinch and double-click on the viewport; wheel and touchmove are not passive, so the page does not scroll with them. */
function viewportInput(model: ZoomModel): (element: Element) => () => void {
  return (element) => {
    const viewport = element as HTMLElement;
    const wheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = viewport.getBoundingClientRect();
      model.zoomBy(event.deltaY < 0 ? ZOOM_FACTOR : 1 / ZOOM_FACTOR, event.clientX - rect.left - rect.width / 2, event.clientY - rect.top - rect.height / 2);
    };
    const down = (event: MouseEvent) => {
      if (event.target instanceof Element && event.target.closest('button')) return;
      event.preventDefault();
      model.startDrag(event.clientX, event.clientY);
    };
    const move = (event: MouseEvent) => model.drag(event.clientX, event.clientY);
    const up = () => model.endDrag();
    const touchStart = (event: TouchEvent) => {
      const [first, second] = [event.touches[0], event.touches[1]];
      if (event.touches.length === 1 && first) model.startDrag(first.clientX, first.clientY);
      else if (event.touches.length === 2 && first && second) model.pinchTo(Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY));
    };
    const touchMove = (event: TouchEvent) => {
      event.preventDefault();
      const [first, second] = [event.touches[0], event.touches[1]];
      if (event.touches.length === 1 && first) model.drag(first.clientX, first.clientY);
      else if (event.touches.length === 2 && first && second) model.pinchTo(Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY));
    };
    const reset = () => model.reset();
    viewport.addEventListener('wheel', wheel, { passive: false });
    viewport.addEventListener('mousedown', down);
    viewport.addEventListener('dblclick', reset);
    viewport.addEventListener('touchstart', touchStart, { passive: true });
    viewport.addEventListener('touchmove', touchMove, { passive: false });
    viewport.addEventListener('touchend', up);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      viewport.removeEventListener('wheel', wheel);
      viewport.removeEventListener('mousedown', down);
      viewport.removeEventListener('dblclick', reset);
      viewport.removeEventListener('touchstart', touchStart);
      viewport.removeEventListener('touchmove', touchMove);
      viewport.removeEventListener('touchend', up);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  };
}

/**
 * The dialog while it is open: focus kept inside it and given back to the
 * trigger when it closes, its keys, and a click on the backdrop closing it.
 */
function dialogBehaviour(model: ZoomModel): (element: Element) => () => void {
  return (element) => {
    const dialog = element as HTMLElement;
    const keys = (event: KeyboardEvent) => model.key(event);
    const backdrop = (event: MouseEvent) => {
      if (event.target === dialog || (event.target instanceof Element && event.target.classList.contains('docs-zoom-viewport'))) model.close();
    };
    dialog.addEventListener('keydown', keys);
    dialog.addEventListener('click', backdrop);
    let release: (() => void) | null = null;
    const stop = effect(() => {
      if (model.open.get()) {
        release ??= trapFocus(dialog);
        dialog.focus();
      } else if (release) {
        release();
        release = null;
      }
    });
    return () => {
      stop();
      release?.();
      dialog.removeEventListener('keydown', keys);
      dialog.removeEventListener('click', backdrop);
    };
  };
}

/** The zoom view: toolbar, the pannable stage, and the floating controls. */
export default function ZoomView({ model }: { readonly model: ZoomModel }) @{
  <div class={() => (model.open.get() ? 'docs-zoom-overlay docs-zoom-open' : 'docs-zoom-overlay')} role="dialog" aria-modal="true" aria-label="Diagram zoom view" tabindex="-1" hidden={() => !model.open.get() && model.content.get() === null} ref={dialogBehaviour(model)}>
    <div class="docs-zoom-toolbar">
      <span class="docs-zoom-label">{() => model.label.get()}</span>
      <span class="docs-zoom-hint">Scroll to zoom · Drag to pan · Double-click to reset · Esc to close</span>
      <IconButton label="Close (Esc)" d={CLOSE} className="docs-zoom-close-btn" onPress={() => model.close()} />
    </div>
    <div class={() => (model.dragging.get() ? 'docs-zoom-viewport cursor-grabbing' : 'docs-zoom-viewport')} ref={viewportInput(model)}>
      <div class="docs-zoom-stage" style:transform={() => model.transform.get()}>
        @if (model.content.get()?.state === 'loading') {
          <div class="docs-zoom-loading">Loading…</div>
        } @else if (model.content.get()?.state === 'error') {
          <div class="docs-zoom-error">
            Could not load diagram
            <br />
            <small>{() => { const content = model.content.get(); return content?.state === 'error' ? content.message : ''; }}</small>
          </div>
        } @else if (model.content.get()?.state === 'ready') {
          <>{() => { const content = model.content.get(); return content?.state === 'ready' ? content.svg : null; }}</>
        }
      </div>
    </div>
    <div class="docs-zoom-controls">
      <IconButton label="Zoom in (+)" d={ZOOM_IN} onPress={() => model.zoomBy(1 + ZOOM_STEP)} />
      <IconButton label="Reset (0)" d={RESET} onPress={() => model.reset()} />
      <IconButton label="Zoom out (-)" d={ZOOM_OUT} onPress={() => model.zoomBy(1 - ZOOM_STEP)} />
    </div>
  </div>
}
